import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import ConditionalTooltip from '../ConditionalTooltip';
import { FORM_FIELD_COMPONENT } from '../editors/Editor';
import SliderEditor from '../editors/SliderEditor';
import DefaultValueEditor from './DefaultValueEditor';
import ArrayEditor from '../editors/ArrayEditor';
import DurationEditor from '../editors/DurationEditor';
import BooleanEditor from '../editors/BooleanEditor';
import TextFieldEditor from '../editors/TextFieldEditor';
import { DateFormatter } from '../../services/Formatters';
import './FieldComponent.scss';

const FormSliderEditor = new SliderEditor('formSlider').getComponent(FORM_FIELD_COMPONENT, true);
const FormArrayEditor = new ArrayEditor().getComponent(FORM_FIELD_COMPONENT, true);
const FormBooleanEditor = new BooleanEditor().getComponent(FORM_FIELD_COMPONENT, true);
const FormDurationEditor = new DurationEditor().getComponent(FORM_FIELD_COMPONENT, true);
const FormTextFieldditor = new TextFieldEditor().getComponent(FORM_FIELD_COMPONENT, true);

const FieldComponent = (props) => {
  const {
    data,
    readOnly,
    property,
    onPropertyChange,
    useConditionalEditing,
    schemaService,
    viewService,
    fullWidth,
    fullHeight,
  } = props;
  const adornments = {};
  let value;
  if (data) {
    value = property.getValue ? property.getValue(data) : data[property.name];
    value = property.formatValue
      ? property.formatValue(value, data, property.name, { ...property, adornments })
      : value;
    if (value === undefined) {
      const { defaultValue } = property || {};
      value = defaultValue;
    }
  }
  const InputProps = {
    readOnly: Boolean(readOnly === undefined ? true : readOnly),
    startAdornment: adornments.prefix ? <InputAdornment position="start">{adornments.prefix}</InputAdornment> : undefined,
    endAdornment: adornments.suffix ? <InputAdornment position="end">{adornments.suffix}</InputAdornment> : undefined,
    inputProps: (property.type === 'integer' || property.type === 'number')
          ? { style: { textAlign: 'right' } } : undefined,
    fullWidth: false,
  };
  return (
    <div key={property.name} className={classnames('tuc-form-field', property.className, fullWidth && 'tuc-form-field-fullwidth', fullHeight && 'tuc-form-field-fullheight')}>
      <DefaultValueEditor
        value={value}
        onChange={onPropertyChange}
        readOnly={readOnly}
        property={property}
        indentIfNA={useConditionalEditing && (property.type !== 'boolean')}
      >
        {(() => {
          switch (property.type) {
            case 'boolean':
              return (
                <FormBooleanEditor
                  description={property.description}
                  property={property}
                  object={data}
                  value={value}
                  onChange={onPropertyChange}
                  label={property.label}
                  readOnly={readOnly}
                  editing
                  form
                />
              );
            case 'percentage':
              return (
                <ConditionalTooltip title={property.description}>
                  <FormSliderEditor
                    readOnly={readOnly}
                    value={value}
                    property={property}
                    editing
                    onChange={onPropertyChange}
                    form
                  />
                </ConditionalTooltip>
              );
            case 'integer':
            case 'number':
            case 'string':
              return (
                <FormTextFieldditor
                  id={property.name}
                  label={property.label}
                  value={value}
                  object={data}
                  type={property.type}
                  editing
                  property={property}
                  onChange={onPropertyChange}
                  readOnly={readOnly}
                  form
                />
              );
            case 'date':
              return (
                <TextField
                  id={property.name}
                  label={property.label}
                  readOnly={readOnly}
                  value={data && value && (readOnly ? DateFormatter(value, data, property.name, props) : value)}
                  margin="normal"
                  onChange={onPropertyChange}
                  InputProps={InputProps}
                  helperText={property.description}
                />
              );
            case 'array':
              return (
                <FormArrayEditor
                  id={property.name}
                  label={property.label}
                  readOnly={readOnly}
                  value={data && value}
                  onChange={onPropertyChange}
                  description={property.description}
                  form
                />
              );
              case 'duration':
                return (
                  <FormDurationEditor
                    id={property.name}
                    label={property.label}
                    readOnly={readOnly}
                    value={data && value}
                    onChange={onPropertyChange}
                    description={property.description}
                    property={property}
                    form
                  />
                );
            default:
            {
              const editor = viewService.getEditor(property.type);
              if (!editor) {
                console.log(`No editor for property ${JSON.stringify(property)}`);
              }
              const Cmp = editor.getComponent(FORM_FIELD_COMPONENT, true);
              if (Cmp) {
                return (
                  <Cmp
                    {...props}
                    id={property.name}
                    label={property.label}
                    description={property.description}
                    value={data && value}
                    onChange={onPropertyChange}
                    readOnly={readOnly}
                    type={property.type}
                    schemaService={schemaService}
                    property={property}
                    object={data}
                    form
                  />
                );
              }
              console.error(`No editor found for property ${property.name} of type ${property.type} `);
            }
            return null;
          }
        })()}
      </DefaultValueEditor>
    </div>
  );
};

FieldComponent.propTypes = {
  data: PropTypes.shape({}).isRequired,
  property: PropTypes.shape({
    name: PropTypes.string,
    getValue: PropTypes.func,
    type: PropTypes.string,
    description: PropTypes.string,
    label: PropTypes.string,
    className: PropTypes.string,
    formatValue: PropTypes.func,
  }).isRequired,
  readOnly: PropTypes.bool,
  useConditionalEditing: PropTypes.bool,
  onPropertyChange: PropTypes.func.isRequired,
  schemaService: PropTypes.shape({}),
  viewService: PropTypes.shape({
    getEditor: PropTypes.func,
  }),
  fullWidth: PropTypes.bool,
  fullHeight: PropTypes.bool,
};

FieldComponent.defaultProps = {
  readOnly: false,
  useConditionalEditing: false,
  schemaService: undefined,
  viewService: undefined,
  fullWidth: false,
  fullHeight: false,
};

export default FieldComponent;
